import React, { useEffect } from 'react';
import { useIntl } from "react-intl";
import agentRepository from "@/repository/agentRepository";
import { useAgentSummary, useDeploymentHistoryPage, usePaginatedTrainingHistories } from "@/query/deployment";
import { useAgentId, useModal, useWebsocket } from "@/hook";
import Button from "@/component/Button";
import { UploadModal } from "@/component/modal";
import { DownloadIcon, IntentIcon, UploadIcon } from "@/assets/svgs/icons";

import TrainingHistoryTable from "./component/TrainingHistoryTable";
import DeploymentTrainingModal, { DEPLOYMENT_TRAINING_MODAL_ID } from "./component/DeploymentTrainingModal";


const AGENT_UPLOAD_MODAL_ID = "agent-upload-modal";

const DeploymentView: React.FC = () => {
    const intl = useIntl();
    const agentId = useAgentId();
    const modal = useModal();
    const websocket = useWebsocket();

    const { data: summary, refetch: refetchSummary } = useAgentSummary(agentId);
    const { data: trainingHistoryPage, refetch: refetchTrainingHistory } = usePaginatedTrainingHistories(agentId, 0, 20);
    const { data: deploymentHistoryPage, refetch: refetchDeploymentHistory } = useDeploymentHistoryPage(agentId, 0, 1);

    const onClickUpload = async (file: File, isCleanImport: boolean) => {
        return await agentRepository.import(agentId, file, isCleanImport);
    };

    useEffect(() => {
        const refetch = async () => {
            await refetchSummary();
            await refetchTrainingHistory();
            await refetchDeploymentHistory();
        };

        websocket.addEventListener("agent_summary", refetch);
        return () => {
            websocket.removeEventListener("agent_summary", refetch);
        };
    }, [websocket, refetchSummary, refetchTrainingHistory, refetchDeploymentHistory]);

    if (!summary) {
        return null;
    }
    return (<>
        <DeploymentTrainingModal />
        <UploadModal id={AGENT_UPLOAD_MODAL_ID}
                     label={intl.formatMessage({ id: "i10030" })}
                     cleanImportText={intl.formatMessage({ id: "i10038" })}
                     upload={onClickUpload}
        />

        <div className="agent-content-layout flex flex-col gap-3">
            <div className="w-md-page flex justify-between">
                <div role="group"
                     aria-label="version"
                     className="w-[344px] h-12 flex border border-black-200 rounded-[6px] bg-white"
                >
                    <div className="w-1/2 px-6 flex justify-between items-center">
                        <div className="text-black-500 text-[14px] font-[400]">
                            {intl.formatMessage({ id: "i10014" })}
                        </div>
                        <div className="text-black-500 text-[20px] font-[600]"
                             data-testid="learning-version"
                        >
                            {summary.sandbox_version.version
                                ? (<>
                                    <span className="text-[18px]">v</span>
                                    {summary.sandbox_version.version}
                                </>) 
                                : "-"
                            }
                        </div>
                    </div>

                    <div className="w-px h-full bg-black-200" />

                    <div className="w-1/2 px-6 flex justify-between items-center">
                        <div className="text-black-500 text-[14px] font-[400]">
                            {intl.formatMessage({ id: "i10015" })}
                        </div>
                        <div className="text-black-500 text-[20px] font-[600]"
                             data-testid="deployment-version"
                        >
                            {summary.service_version.version
                                ? (<>
                                    <span className="text-[18px]">v</span>
                                    {summary.service_version.version}
                                </>) 
                                : "-"
                            }
                        </div>
                    </div>
                </div>

                <div className="subtitle-container-btn-group">
                    <Button variant="text"
                            onClick={() => modal.open(DEPLOYMENT_TRAINING_MODAL_ID)}
                    >
                        <IntentIcon className="size-4" />
                        {intl.formatMessage({ id: "i10002" })}
                    </Button>
                    <Button variant="text"
                            onClick={() => modal.open(AGENT_UPLOAD_MODAL_ID)}
                    >
                        <UploadIcon className="size-4" />
                        {intl.formatMessage({ id: "b0004" })}
                    </Button>
                    <Button variant="text"
                            onClick={() => agentRepository.export(agentId)}
                    >
                        <DownloadIcon className="size-4" />
                        {intl.formatMessage({ id: "b0005" })}
                    </Button>
                </div>
            </div>

            <div className="w-md-page flex-1 min-h-0 max-h-fit">
                <TrainingHistoryTable trainingHistories={trainingHistoryPage?.content}
                                      deployHistory={deploymentHistoryPage?.content[0]}
                />
            </div>
        </div>
    </>);
};


export default DeploymentView;
